import { useQuery } from "@tanstack/react-query";

/** A cached Coach Manager Client row — see `src/db/schema/coach-manager-clients.ts`. */
export interface CachedClient {
  id: string;
  buInstanceId: string;
  clientId: string;
  title: string | null;
  initials: string | null;
  firstName: string | null;
  surname: string | null;
  company: string | null;
  address1: string | null;
  address2: string | null;
  address3: string | null;
  address4: string | null;
  postcode: string | null;
  telNo1: string | null;
  email: string | null;
  vatNo: string | null;
  suspend: boolean;
  deleted: boolean;
  sourceDateCreated: string | null;
  sourceLastUpdated: string | null;
  lastSeenAt: string;
  createdAt: string;
  updatedAt: string;
}

async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  const json = await res.json().catch(() => null);
  return json?.error ?? fallback;
}

/**
 * Locally-cached clients for a BU instance — the Copy Contract Target
 * Client picker's data source. Pure Postgres read, no SOAP call. Enabled as
 * soon as a `buInstanceId` is present (NOT gated on `search` having any
 * text) — with no search term this returns the most-recently-seen cached
 * clients for that BU, which is what lets the picker populate immediately
 * on BU selection. Coach Manager's `ClientSearch` itself can't do this (it
 * requires at least one non-empty search field), so this deliberately reads
 * the cache populated by Client Search rather than calling it live.
 */
export function useCachedClients(buInstanceId: string | null, search: string) {
  return useQuery({
    queryKey: ["cached-clients", buInstanceId, search] as const,
    queryFn: async (): Promise<CachedClient[]> => {
      const params = new URLSearchParams({ buInstanceId: buInstanceId ?? "" });
      if (search.trim()) params.set("search", search.trim());
      const res = await fetch(`/api/coach-manager/clients/cached?${params}`);
      if (!res.ok)
        throw new Error(await extractErrorMessage(res, "Failed to fetch cached clients"));
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(buInstanceId),
  });
}
